<template>
  <DashboardLayout>
    <div class="dashboard-container">
      <!-- Welcome Header -->
      <div class="mb-6 lg:mb-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              class="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
              {{ getGreeting() }}, {{ userName }}!
            </h1>
            <p class="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">{{ currentDate }}
            </p>
          </div>

          <!-- Actions & Period Selector -->
          <div class="flex flex-wrap items-center gap-3">
            <!-- Period Selector -->
            <div class="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm">
              <div class="flex items-center px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-100">
                <CalendarDays class="h-4 w-4 text-slate-400 mr-2" />
                <select v-model="selectedMonth"
                  class="bg-transparent text-sm font-bold text-slate-700 focus:outline-none cursor-pointer">
                  <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
                </select>
              </div>
              <div class="flex items-center px-3 py-1.5 bg-slate-50 rounded-xl border border-slate-100">
                <select v-model="selectedYear"
                  class="bg-transparent text-sm font-bold text-slate-700 focus:outline-none cursor-pointer">
                  <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
                </select>
              </div>
              <Button size="icon" variant="ghost" @click="refreshData"
                class="h-9 w-9 rounded-xl hover:bg-slate-100 transition-colors" title="Refrescar datos">
                <RefreshCw class="h-4 w-4 text-slate-500" :class="{ 'animate-spin': loading }" />
              </Button>
            </div>

            <Button @click="router.push(`/companies/${selectedCompanyId}/billing/generate/invoice`)"
              class="flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white rounded-full px-5 h-11 shadow-lg shadow-cyan-200 transition-all font-bold">
              <Plus class="h-4 w-4" />
              Nueva Factura
            </Button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="min-h-[60vh] flex items-center justify-center">
        <LoadingScreen />
      </div>

      <!-- Empty State & Onboarding (First Time or Setup Incomplete) -->
      <div v-else-if="showOnboarding" class="max-w-5xl mx-auto px-4">
        <!-- Welcome Banner -->
        <div v-if="isFirstTime"
          class="rounded-xl border-2 border-dashed border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-teal-50 p-6 sm:p-8 lg:p-12 text-center mb-8">
          <div
            class="mx-auto w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center mb-4 sm:mb-6">
            <Rocket class="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
          </div>
          <h2
            class="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
            ¡Comienza tu viaje con ZENTRY!
          </h2>
          <p class="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
            Configura tu primera empresa para empezar a gestionar tus documentos electrónicos.
          </p>
        </div>

        <!-- Onboarding Steps Component -->
        <OnboardingSteps :companies-count="stats.companies.total" :is-configured="isCompanyConfigured"
          :current-company-id="selectedCompanyId" />
      </div>

      <!-- Main Dashboard Content -->
      <div v-else class="space-y-8">

        <!-- 1. Financial Overview Cards (Top Priority) -->
        <!-- 1. Financial Overview Cards (Top Priority) -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Total Facturado -->
          <div
            class="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-1 group">
            <div
              class="absolute right-0 top-0 h-24 w-24 translate-x-8 translate-y--8 rounded-full bg-emerald-500/10 blur-2xl transition-all group-hover:bg-emerald-500/20">
            </div>
            <div class="relative flex items-start justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Ingresos</p>
                <div class="mt-2 flex items-baseline gap-2">
                  <h3 class="text-2xl font-bold text-slate-900">${{ formatNumberCompact(stats.financial.total_invoiced
                    || 0) }}</h3>
                </div>
                <p class="mt-1 flex items-center text-xs font-medium text-emerald-600">
                  <TrendingUp class="mr-1 h-3 w-3" />
                  +{{ stats.documents.this_month }} docs este mes
                </p>
              </div>
              <div class="rounded-lg bg-emerald-50 p-2.5 text-emerald-600 transition-colors group-hover:bg-emerald-100">
                <DollarSign class="h-6 w-6" />
              </div>
            </div>
          </div>

          <!-- Total Gastos -->
          <div
            class="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-1 group">
            <div
              class="absolute right-0 top-0 h-24 w-24 translate-x-8 translate-y--8 rounded-full bg-rose-500/10 blur-2xl transition-all group-hover:bg-rose-500/20">
            </div>
            <div class="relative flex items-start justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Gastos</p>
                <div class="mt-2 flex items-baseline gap-2">
                  <h3 class="text-2xl font-bold text-slate-900">${{ formatNumberCompact(stats.financial.total_expenses
                    || 0) }}</h3>
                </div>
                <p class="mt-1 flex items-center text-xs font-medium text-rose-600">
                  <TrendingDown class="mr-1 h-3 w-3" />
                  Gastos registrados
                </p>
              </div>
              <div class="rounded-lg bg-rose-50 p-2.5 text-rose-600 transition-colors group-hover:bg-rose-100">
                <TrendingDown class="h-6 w-6" />
              </div>
            </div>
          </div>

          <!-- Procesados IA -->
          <div
            class="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-1 group">
            <div
              class="absolute right-0 top-0 h-24 w-24 translate-x-8 translate-y--8 rounded-full bg-purple-500/10 blur-2xl transition-all group-hover:bg-purple-500/20">
            </div>
            <div class="relative flex items-start justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Procesados IA</p>
                <div class="mt-2 flex items-baseline gap-2">
                  <h3 class="text-2xl font-bold text-slate-900">{{ stats.documents.analyzed }}</h3>
                  <span class="text-xs text-slate-400">docs</span>
                </div>
                <p class="mt-1 text-xs font-medium text-purple-600" v-if="stats.documents.pending_analysis > 0">
                  {{ stats.documents.pending_analysis }} analizados
                </p>
                <p class="mt-1 text-xs font-medium text-slate-400" v-else>
                  Todo al día
                </p>
              </div>
              <div class="rounded-lg bg-purple-50 p-2.5 text-purple-600 transition-colors group-hover:bg-purple-100">
                <Brain class="h-6 w-6" />
              </div>
            </div>
          </div>

          <!-- Total Documentos -->
          <div
            class="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-1 group">
            <div
              class="absolute right-0 top-0 h-24 w-24 translate-x-8 translate-y--8 rounded-full bg-blue-500/10 blur-2xl transition-all group-hover:bg-blue-500/20">
            </div>
            <div class="relative flex items-start justify-between">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">Documentos</p>
                <div class="mt-2 flex items-baseline gap-2">
                  <h3 class="text-2xl font-bold text-slate-900">{{ stats.documents.total }}</h3>
                  <span class="text-xs text-slate-400">total</span>
                </div>
                <p class="mt-1 text-xs font-medium text-blue-600">
                  Gestión total
                </p>
              </div>
              <div class="rounded-lg bg-blue-50 p-2.5 text-blue-600 transition-colors group-hover:bg-blue-100">
                <FileText class="h-6 w-6" />
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 2. Monthly History Chart (Left - Wider) -->
          <div class="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                <BarChart3 class="h-5 w-5 text-cyan-600" />
                Historial de Ingresos
              </h3>
              <div class="text-sm text-slate-500">Últimos 6 meses</div>
            </div>

            <!-- Custom CSS Chart (Double Bar) -->
            <div class="relative h-64 w-full mt-4">
              <div v-if="!stats.monthly_stats || stats.monthly_stats.length === 0"
                class="flex h-full items-center justify-center text-slate-400">
                No hay datos suficientes
              </div>
              <div v-else class="flex items-end justify-between h-full gap-2 sm:gap-4 pl-0 sm:pl-4">
                <div v-for="(month, index) in stats.monthly_stats.slice().reverse()" :key="index"
                  class="flex flex-col items-center flex-1 h-full justify-end group cursor-pointer">

                  <!-- Tooltip -->
                  <div
                    class="opacity-0 group-hover:opacity-100 transition-all duration-300 absolute -top-12 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs py-2 px-3 rounded-lg shadow-xl pointer-events-none whitespace-nowrap z-20 flex flex-col items-center gap-1 border border-slate-700">
                    <div class="font-bold border-b border-slate-700 pb-1 mb-1 w-full text-center">{{
                      translateMonth(month.month_short) }}</div>
                    <div class="flex items-center gap-2">
                      <span class="w-2 h-2 rounded-full bg-cyan-500"></span>
                      <span>Ing: ${{ formatNumberCompact(month.total) }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="w-2 h-2 rounded-full bg-rose-500"></span>
                      <span>Gas: ${{ formatNumberCompact(month.expenses || 0) }}</span>
                    </div>
                  </div>

                  <!-- Bars Container -->
                  <div class="flex items-end gap-1 w-full justify-center h-full relative">
                    <!-- Income Bar -->
                    <div
                      class="w-full max-w-[16px] sm:max-w-[24px] bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t-sm transition-all duration-500 group-hover:from-cyan-500 group-hover:to-cyan-300 relative group-hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                      :style="{ height: (month.total / Math.max(...stats.monthly_stats.map(m => Math.max(m.total, m.expenses || 0)), 1) * 100) + '%' }">
                    </div>

                    <!-- Expense Bar -->
                    <div
                      class="w-full max-w-[16px] sm:max-w-[24px] bg-gradient-to-t from-rose-600 to-rose-400 rounded-t-sm transition-all duration-500 group-hover:from-rose-500 group-hover:to-rose-300 relative group-hover:shadow-[0_0_15px_rgba(244,63,94,0.5)]"
                      :style="{ height: ((month.expenses || 0) / Math.max(...stats.monthly_stats.map(m => Math.max(m.total, m.expenses || 0)), 1) * 100) + '%' }">
                    </div>
                  </div>

                  <!-- Label -->
                  <div
                    class="text-[10px] sm:text-xs text-slate-500 mt-3 font-bold tracking-wide w-full text-center uppercase group-hover:text-cyan-600 transition-colors">
                    {{ translateMonth(month.month_short) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 3. Quick Actions & Document Types (Right - Narrower) -->
          <div class="space-y-6">
            <!-- Quick Actions -->
            <div class="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
              <h3 class="text-base font-semibold mb-4 text-slate-800">Accesos Directos</h3>
              <div class="grid grid-cols-2 gap-3">
                <button @click="goToCompanies"
                  class="flex flex-col items-center justify-center p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-cyan-50 hover:border-cyan-200 transition-all text-center group">
                  <Building2 class="h-6 w-6 text-slate-600 group-hover:text-cyan-600 mb-2" />
                  <span class="text-xs font-medium text-slate-700 group-hover:text-cyan-700">Empresas</span>
                </button>
                <button @click="goToSales"
                  class="flex flex-col items-center justify-center p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-green-50 hover:border-green-200 transition-all text-center group">
                  <TrendingUp class="h-6 w-6 text-slate-600 group-hover:text-green-600 mb-2" />
                  <span class="text-xs font-medium text-slate-700 group-hover:text-green-700">Facturas</span>
                </button>
                <button @click="goToExpenses"
                  class="flex flex-col items-center justify-center p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-red-50 hover:border-red-200 transition-all text-center group">
                  <TrendingDown class="h-6 w-6 text-slate-600 group-hover:text-red-600 mb-2" />
                  <span class="text-xs font-medium text-slate-700 group-hover:text-red-700">Gastos</span>
                </button>
                <button @click="goToSettings"
                  class="flex flex-col items-center justify-center p-3 rounded-lg border border-slate-100 bg-slate-50 hover:bg-gray-100 hover:border-gray-200 transition-all text-center group">
                  <Settings class="h-6 w-6 text-slate-600 group-hover:text-gray-800 mb-2" />
                  <span class="text-xs font-medium text-slate-700 group-hover:text-gray-900">Config</span>
                </button>
              </div>
            </div>

            <!-- By Type -->
            <div class="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
              <h3 class="text-base font-semibold mb-4 text-slate-800 flex items-center gap-2">
                <TrendingDown class="h-4 w-4 text-red-500" />
                Gastos por Tipo
              </h3>
              <div class="space-y-3">
                <div v-for="type in stats.by_type.slice(0, 4)" :key="type.type"
                  class="flex items-center justify-between text-sm">
                  <div class="flex items-center gap-2">
                    <div class="w-2 h-2 rounded-full" :class="{
                      'bg-blue-500': type.type === 'FACTURA',
                      'bg-red-500': type.type === 'COMPROBANTE_RETENCION',
                      'bg-yellow-500': type.type === 'NOTA_CREDITO',
                      'bg-gray-400': !['FACTURA', 'COMPROBANTE_RETENCION', 'NOTA_CREDITO'].includes(type.type)
                    }"></div>
                    <span class="text-slate-600 truncate max-w-[100px]">{{ formatDocTypeShort(type.type) }}</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <span class="font-bold text-slate-700">{{ type.count }}</span>
                    <span class="text-xs text-slate-400 w-16 text-right">${{ formatNumberCompact(type.total) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 4. Expense Analysis (Below Income History inside same grid) -->
          <div v-if="expenseData && expenseData.categories.length > 0"
            class="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200 animate-in">
            <div class="flex items-center justify-between mb-8">
              <h3 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                <PieChart class="h-5 w-5 text-rose-500" />
                Análisis de Gastos por Categoría
              </h3>
              <div class="text-xs font-bold text-rose-600 uppercase tracking-widest">Este Mes</div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <ExpensesDonutChart :categories="expenseData.categories" :total="expenseData.total_expenses"
                v-model="hoveredCategoryId" :showLegend="true" size="md" />

              <div class="space-y-4">
                <div v-for="cat in expenseData.categories.slice(0, 5)" :key="cat.category_id"
                  class="flex items-center justify-between group cursor-default p-2 rounded-lg transition-all duration-300"
                  :class="{ 'bg-slate-50 shadow-sm scale-[1.02]': hoveredCategoryId === cat.category_id, 'opacity-40': hoveredCategoryId && hoveredCategoryId !== cat.category_id }"
                  @mouseenter="hoveredCategoryId = cat.category_id" @mouseleave="hoveredCategoryId = null">
                  <div class="flex items-center gap-3">
                    <div class="w-1.5 h-8 rounded-full" :style="{ backgroundColor: cat.color }"></div>
                    <div>
                      <div class="text-sm font-bold text-slate-700">{{ cat.category_name }}</div>
                      <div class="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{{ cat.count }}
                        documentos</div>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-sm font-black text-slate-900 font-mono">${{ cat.total }}</div>
                    <div class="text-[10px] font-bold text-slate-400">{{ Math.round((cat.total /
                      expenseData.total_expenses) * 100) }}%</div>
                  </div>
                </div>

                <Button variant="ghost" size="sm" @click="router.push({ path: '/reports', query: { tab: 'expenses' } })"
                  class="w-full text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 border border-dashed border-rose-200 mt-2">
                  Ver reporte completo de gastos
                </Button>
              </div>
            </div>
          </div>

          <!-- 5. Business Health / Margin Info (Filling the 3rd column space) -->
          <div v-if="expenseData && expenseData.categories.length > 0"
            class="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col h-full animate-in">
            <h3 class="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-6">
              <Zap class="h-4 w-4 text-amber-500" />
              Balance Mensual
            </h3>

            <div class="space-y-6 flex-1 flex flex-col">
              <div class="flex items-center justify-between">
                <div class="flex flex-col">
                  <span class="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Ingresos
                    Estimados</span>
                  <span class="text-lg font-black text-slate-800">${{ formatNumber(stats.financial.total_invoiced)
                  }}</span>
                </div>
                <div
                  class="h-10 w-10 rounded-xl bg-cyan-50 flex items-center justify-center text-cyan-600 shadow-sm shadow-cyan-100/50">
                  <ArrowUpRight class="h-5 w-5" />
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex flex-col">
                  <span class="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">Gastos del Mes</span>
                  <span class="text-lg font-black text-rose-600">${{ formatNumber(expenseData.total_expenses) }}</span>
                </div>
                <div
                  class="h-10 w-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600 shadow-sm shadow-rose-100/50">
                  <ArrowDownRight class="h-5 w-5" />
                </div>
              </div>

              <div class="pt-6 border-t border-dashed border-slate-100 mt-auto">
                <div
                  class="flex flex-col text-center p-4 bg-slate-50 rounded-2xl border border-slate-100 relative overflow-hidden group hover:bg-white transition-colors duration-300">
                  <div
                    class="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-transparent to-slate-100/50 -mr-8 -mt-8 rounded-full transition-transform group-hover:scale-150">
                  </div>

                  <span class="text-[9px] text-slate-400 font-bold uppercase tracking-widest relative z-10">Flujo Bruto
                    Generado</span>
                  <span class="text-2xl font-black relative z-10"
                    :class="stats.financial.total_invoiced - expenseData.total_expenses >= 0 ? 'text-emerald-600' : 'text-rose-600'">
                    ${{ formatNumber(stats.financial.total_invoiced - expenseData.total_expenses) }}
                  </span>

                  <div class="mt-3 flex items-center justify-center gap-2 relative z-10">
                    <span
                      class="text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest text-white shadow-md shadow-emerald-200"
                      :class="stats.financial.total_invoiced - expenseData.total_expenses >= 0 ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gradient-to-r from-rose-500 to-orange-600 shadow-rose-200'">
                      {{ stats.financial.total_invoiced > 0 ? Math.round(((stats.financial.total_invoiced -
                        expenseData.total_expenses) / stats.financial.total_invoiced) * 100) : 0 }}% Rentabilidad
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Activity (Reimagined with Personality) -->
        <div
          class="rounded-2xl border border-slate-200/60 bg-white shadow-xl shadow-slate-200/40 backdrop-blur-xl overflow-hidden">
          <div
            class="p-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-white to-slate-50/50">
            <div>
              <h3 class="text-xl font-bold text-slate-800 tracking-tight">Movimientos Recientes</h3>
              <p class="text-sm text-slate-500 mt-1">Tu actividad en tiempo real</p>
            </div>
            <Button variant="ghost" size="sm" @click="goToDocuments"
              class="text-xs font-semibold text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 transition-colors rounded-full px-4">
              Ver historial completo
              <ArrowUpRight class="ml-1 h-4 w-4" />
            </Button>
          </div>

          <div class="p-0">
            <div v-if="recentActivity.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
              <div class="h-16 w-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
                <Inbox class="h-8 w-8 text-slate-300" />
              </div>
              <h4 class="text-slate-900 font-medium">Sin actividad</h4>
              <p class="text-slate-500 text-sm mt-1">No hay movimientos recientes para mostrar.</p>
            </div>

            <div v-else class="divide-y divide-slate-100">
              <div v-for="(activity, index) in recentActivity" :key="activity.id"
                class="group flex items-center justify-between p-5 hover:bg-slate-50/80 transition-all duration-300 cursor-pointer border-l-4 border-transparent hover:border-cyan-500">

                <!-- Left: Avatar & Info -->
                <div class="flex items-center gap-4">
                  <!-- Avatar with Ring -->
                  <div class="relative">
                    <div
                      class="h-12 w-12 rounded-2xl flex items-center justify-center text-sm font-bold text-white shadow-md ring-4 ring-white transition-transform group-hover:scale-105"
                      :class="[
                        activity.role === 'issued'
                          ? 'bg-gradient-to-br from-cyan-400 to-blue-600'
                          : 'bg-gradient-to-br from-rose-400 to-orange-500'
                      ]">
                      {{ activity.issuer.substring(0, 2).toUpperCase() }}
                    </div>
                    <div
                      class="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white flex items-center justify-center bg-white shadow-sm"
                      :title="activity.role === 'issued' ? 'Ingreso' : 'Egreso'">
                      <TrendingUp v-if="activity.role === 'issued'" class="h-3 w-3 text-emerald-600" />
                      <TrendingDown v-else class="h-3 w-3 text-rose-600" />
                    </div>
                  </div>

                  <div>
                    <div class="flex items-center gap-2">
                      <h4 class="font-bold text-slate-800 text-sm group-hover:text-cyan-700 transition-colors">{{
                        activity.issuer }}</h4>
                      <span v-if="index === 0"
                        class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan-100 text-cyan-700 uppercase tracking-wide">Nuevo</span>
                    </div>
                    <div class="flex items-center gap-2 mt-1">
                      <span class="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                        {{ formatDocTypeShort(activity.type) }}
                      </span>
                      <span class="text-xs text-slate-400 flex items-center">
                        <CalendarDays class="h-3 w-3 mr-1" /> {{ activity.date }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Right: Amount & Status -->
                <div class="flex items-center gap-6">
                  <div class="text-right">
                    <div class="text-base font-bold tracking-tight" :class="{
                      'text-emerald-600': activity.role === 'issued' && activity.type !== 'NOTA_CREDITO',
                      'text-rose-600': activity.role === 'received' || activity.type === 'NOTA_CREDITO'
                    }">
                      {{ (activity.role === 'issued' && activity.type !== 'NOTA_CREDITO') ? '+' : '-' }}${{
                        formatNumber(activity.amount) }}
                    </div>
                    <div class="text-xs font-medium text-slate-400 uppercase tracking-wide">
                      {{ activity.role === 'issued' ? 'Facturado' : 'Retenido' }}
                    </div>
                  </div>

                  <div class="hidden sm:block">
                    <!-- Status Indicator -->
                    <div
                      class="h-8 w-8 rounded-full flex items-center justify-center bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                      title="Autorizado">
                      <Check class="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer Action -->
          <div class="bg-slate-50 p-3 text-center border-t border-slate-100">
            <button class="text-xs font-medium text-slate-500 hover:text-cyan-600 transition-colors">Mostrando últimos 5
              registros</button>
          </div>
        </div>

      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import apiClient, { dashboardService } from '@/services/api'
import { useCompany } from '@/composables/useCompany'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import OnboardingSteps from '@/components/dashboard/OnboardingSteps.vue'
import ExpensesDonutChart from '@/components/dashboard/ExpensesDonutChart.vue'
import LoadingScreen from '@/components/ui/LoadingScreen.vue'
import Button from '@/components/ui/Button.vue'
import { reportService } from '@/services/reportService'
import {
  RefreshCw, Rocket, Building2, Tags, Download, Plus, HelpCircle, BookOpen,
  FileText, Brain, Zap, Settings, FolderPlus, FileDown, Activity, PieChart,
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Landmark, BarChart3,
  CalendarDays, Inbox, Check
} from 'lucide-vue-next'

const router = useRouter()
const authStore = useAuthStore()
const { selectedCompanyId, loadingCompanies, initialized } = useCompany()
const loading = ref(true)

// Month/Year Selection
const selectedMonth = ref(new Date().getMonth() + 1)
const selectedYear = ref(new Date().getFullYear())

const months = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
]

const availableYears = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let i = currentYear; i >= currentYear - 2; i--) {
    years.push(i)
  }
  return years
})

const stats = ref({
  companies: {
    total: 0,
    active: 0,
    with_credentials: 0
  },
  documents: {
    total: 0,
    this_month: 0,
    analyzed: 0,
    pending_analysis: 0
  },
  financial: {
    total_invoiced: 0,
    total_expenses: 0,
    total_taxes: 0,
    currency: 'USD'
  },
  by_type: [],
  recent_activity: [],
  monthly_stats: []
})

const recentActivity = computed(() => stats.value.recent_activity || [])

const userName = computed(() => authStore.user?.name?.split(' ')[0] || 'Usuario')
const currentDate = computed(() => {
  return new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
})

// Expense Analysis Data
const expenseData = ref(null)
const hoveredCategoryId = ref(null)

const isFirstTime = computed(() => {
  return !loading.value && stats.value.companies.total === 0
})

const isCompanyConfigured = computed(() => {
  // Si no tiene empresas, no está configurado
  if (stats.value.companies.total === 0) return false

  // Aquí asumimos que stats.companies.with_credentials nos dice si la firma está ok
  // Opcionalmente podemos chequear si selectedCompanyId tiene firma validada
  // Pero stats.companies.with_credentials es un buen proxy general
  return stats.value.companies.with_credentials > 0
})

const showOnboarding = computed(() => {
  return isFirstTime.value || !isCompanyConfigured.value
})

const getGreeting = () => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Buenos días'
  if (hour < 19) return 'Buenas tardes'
  return 'Buenas noches'
}

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num || 0)
}

const formatDocType = (type) => {
  const types = {
    'FACTURA': 'Facturas',
    'NOTA_CREDITO': 'Notas de Crédito',
    'NOTA_DEBITO': 'Notas de Débito',
    'COMPROBANTE_RETENCION': 'Retenciones',
    'GUIA_REMISION': 'Guías de Remisión',
    'LIQUIDACION_COMPRA': 'Liquidaciones de Compra',
    '01': 'Facturas',
    '03': 'Liquidaciones de Compra',
    '04': 'Notas de Crédito',
    '05': 'Notas de Débito',
    '06': 'Guías de Remisión',
    '07': 'Retenciones'
  }
  return types[type] || type
}

const getPercentage = (count) => {
  const total = stats.value.documents.total
  if (total === 0) return 0
  return Math.round((count / total) * 100)
}

const formatDocTypeShort = (type) => {
  const types = {
    'FACTURA': 'Factura',
    'NOTA_CREDITO': 'N. Crédito',
    'NOTA_DEBITO': 'N. Débito',
    'COMPROBANTE_RETENCION': 'Retención',
    'GUIA_REMISION': 'Guía',
    'LIQUIDACION_COMPRA': 'Liq. Compra',
    '01': 'Factura',
    '03': 'Liq. Compra',
    '04': 'N. Crédito',
    '05': 'N. Débito',
    '06': 'Guía',
    '07': 'Retención'
  }
  return types[type] || type
}

const translateMonth = (shortMonth) => {
  const map = {
    'JAN': 'ENE', 'FEB': 'FEB', 'MAR': 'MAR', 'APR': 'ABR', 'MAY': 'MAY', 'JUN': 'JUN',
    'JUL': 'JUL', 'AUG': 'AGO', 'SEP': 'SEP', 'OCT': 'OCT', 'NOV': 'NOV', 'DEC': 'DIC',
    'Jan': 'ENE', 'Feb': 'FEB', 'Mar': 'MAR', 'Apr': 'ABR', 'May': 'MAY', 'Jun': 'JUN',
    'Jul': 'JUL', 'Aug': 'AGO', 'Sep': 'SEP', 'Oct': 'OCT', 'Nov': 'NOV', 'Dec': 'DIC'
  }
  return map[shortMonth] || shortMonth
}

const formatNumberCompact = (num) => {
  return new Intl.NumberFormat('en-US', {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(num || 0)
}

const fetchDashboardData = async () => {
  loading.value = true
  try {
    const params = {
      month: selectedMonth.value,
      year: selectedYear.value
    }
    if (selectedCompanyId.value) {
      params.company_id = selectedCompanyId.value
    }

    const res = await dashboardService.getStats(params)

    if (res.success) {
      stats.value = res.data
    }
  } catch (error) {
    // Error silenciado para limpieza, ya manejado por loading state
  } finally {
    loading.value = false
  }
}

const fetchExpenseAnalysis = async () => {
  if (!selectedCompanyId.value) return

  const firstDay = new Date(selectedYear.value, selectedMonth.value - 1, 1).toISOString().split('T')[0]
  const lastDay = new Date(selectedYear.value, selectedMonth.value, 0).toISOString().split('T')[0]

  try {
    const response = await reportService.getExpensesReport({
      company_id: selectedCompanyId.value,
      start_date: firstDay,
      end_date: lastDay
    })

    if (response.success) {
      expenseData.value = response.data
    }
  } catch (error) {
    console.error('Error fetching dashboard expense analysis:', error)
  }
}

const refreshData = () => {
  fetchDashboardData()
  fetchExpenseAnalysis()
}
const goToCompanies = () => router.push('/companies')
const goToCategories = () => router.push('/categories')
const goToDocuments = () => router.push('/documents')
const goToSales = () => router.push(`/companies/${selectedCompanyId.value}/billing/documents`)
const goToExpenses = () => router.push({ path: '/documents', query: { type: 'received' } })
const goToAnalysis = () => router.push('/analysis')
const goToAccounts = () => router.push('/accounts')
const goToSettings = () => router.push('/settings')

// Consolidated watcher for data fetching
watch(
  [selectedCompanyId, loadingCompanies, initialized, selectedMonth, selectedYear],
  ([newId, newLoading, isInitialized, newMonth, newYear]) => {
    // Don't fetch if companies are still loading or not initialized
    if (newLoading || !isInitialized) return

    fetchDashboardData()
    fetchExpenseAnalysis()
  },
  { immediate: true }
)
</script>

<style scoped>
.dashboard-container {
  padding-bottom: 2rem;
}

.stats-card {
  border-radius: 0.75rem;
  border-width: 1px;
  background-color: var(--card);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  padding: 1rem;
  transition: box-shadow 0.3s;
}

.stats-card:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

@media (min-width: 640px) {
  .stats-card {
    padding: 1.5rem;
  }
}

.financial-card {
  border-radius: 0.75rem;
  border-width: 2px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
  transition: all 0.3s;
}

.financial-card:hover {
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  transform: translateY(-2px);
}

.quick-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border-width: 1px;
  background-color: var(--card);
  cursor: pointer;
  transition: all 0.3s;
}

.quick-action-btn:hover {
  background-color: var(--accent);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

@media (min-width: 640px) {
  .quick-action-btn {
    gap: 0.5rem;
    padding: 1rem;
  }
}

.quick-action-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
}

@media (min-width: 640px) {
  .quick-action-icon {
    width: 3rem;
    height: 3rem;
  }
}

.quick-action-text {
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.25;
}

@media (min-width: 640px) {
  .quick-action-text {
    font-size: 0.875rem;
  }
}

.info-card {
  border-radius: 0.75rem;
  border-width: 1px;
  background-color: var(--card);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  overflow: hidden;
}

.info-card-header {
  padding: 1rem;
  border-bottom-width: 1px;
  background: linear-gradient(to right, rgb(236 254 255), rgb(204 251 241));
}

@media (min-width: 640px) {
  .info-card-header {
    padding: 1.5rem;
  }
}

.info-card-content {
  padding: 1rem;
  max-height: 300px;
  overflow-y: auto;
}

@media (min-width: 640px) {
  .info-card-content {
    padding: 1.5rem;
    max-height: 350px;
  }
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: background-color 0.3s;
}

.activity-item:hover {
  background-color: var(--accent);
}

@media (min-width: 640px) {
  .activity-item {
    padding: 0.75rem;
  }
}

.activity-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .activity-icon {
    width: 2.5rem;
    height: 2.5rem;
  }
}

.type-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
}

@media (min-width: 640px) {
  .type-item {
    padding: 0.75rem;
  }
}

/* Custom scrollbar */
.info-card-content::-webkit-scrollbar {
  width: 6px;
}

.info-card-content::-webkit-scrollbar-track {
  background-color: transparent;
}

.info-card-content::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 9999px;
}

.info-card-content::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
